import React, { memo, useState, useCallback } from 'react';
import { PlusCircle } from 'lucide-react';
import { Deal, DealService } from '../../lib/types';
import { DealServiceList } from './DealServiceList';
import { ExpandableDescription } from './ExpandableDescription';
import { useDealServiceStore } from '../../lib/stores/dealServiceStore';
import { useApp } from '../../components/AppProvider';

interface DealServicesSectionProps {
  deal: Deal;
  dealServices: DealService[];
  translations: any;
}

export const DealServicesSection: React.FC<DealServicesSectionProps> = memo(({
  deal,
  dealServices,
  translations: t
}) => {
  const { toast, confirm } = useApp();
  const { updateDealService, deleteDealService, fetchDealServices } = useDealServiceStore();
  const [isReordering, setIsReordering] = useState(false);
  
  // Funzione per gestire il riordinamento dei servizi
  const handleReorderServices = async (reorderedServices: DealService[]) => {
    setIsReordering(true);
    
    try {
      // Aggiorniamo ogni servizio con il nuovo valore order
      const updatePromises = reorderedServices.map((service, index) => 
        updateDealService(service.id, { order: index })
      );
      
      await Promise.all(updatePromises);
      
      // Ricarica i servizi per avere l'ordine aggiornato
      await fetchDealServices(deal.id);
      
      toast.success('Ordine dei servizi aggiornato con successo');
    } catch (error) {
      console.error('Errore durante il riordinamento dei servizi:', error);
      toast.error('Si è verificato un errore durante il riordinamento dei servizi');
    } finally {
      setIsReordering(false);
    }
  };

  // Funzione per gestire l'eliminazione di un servizio
  const handleDeleteService = useCallback(async (serviceId: string) => {
    const confirmed = await confirm({
      title: 'Elimina Servizio',
      message: 'Sei sicuro di voler eliminare questo servizio dalla trattativa? Questa azione non può essere annullata.',
      confirmText: 'Elimina',
      cancelText: 'Annulla',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        await deleteDealService(serviceId);
        await fetchDealServices(deal.id);
        toast.success('Servizio eliminato con successo');
      } catch (error) {
        console.error('Error deleting service:', error);
        toast.error('Errore durante l\'eliminazione del servizio');
      }
    }
  }, [deleteDealService, fetchDealServices, deal.id, toast, confirm]);

  // Funzione per gestire l'editing di un servizio
  const handleEditService = useCallback((service: DealService) => {
    toast.info('Modifica il servizio dal carrello nella schermata di editing dell\'offerta');
  }, [toast]);

  // Funzione per gestire la gestione dei costi effettivi
  const handleManageCosts = useCallback(() => {
    // Navigate to actual costs page for this deal
    window.location.href = `/deals/${deal.id}/actual-costs?title=${encodeURIComponent(deal.title || '')}`;
  }, [deal]);
  return (
    <div className="border-t border-black/10 dark:border-white/10">
      {/* Descrizione (utilizzando il componente espandibile) */}
      {deal.description && (
        <div className="px-4 md:px-6 py-4 md:py-5 border-b border-black/10 dark:border-white/10 prose prose-sm dark:prose-invert max-w-none">
          <h3 className="text-base md:text-lg font-medium text-black dark:text-white">{t.descriptionTitle || 'Descrizione'}</h3>
          <ExpandableDescription
            description={deal.description}
            className="mt-2"
            maxHeight={150}
            translations={{
              showMore: t.showMore || 'Mostra di più',
              showLess: t.showLess || 'Mostra meno'
            }}
          />
        </div>
      )}

      {/* Sezione servizi - Notion style */}
      <div className="py-4 md:py-5">
        <div className="flex items-center justify-between mb-3 md:mb-4 px-4 md:px-6">
          <h2 className="text-sm font-medium text-[#37352F] dark:text-white flex items-center">
            {t.services || 'Servizi'}
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#787774] dark:text-gray-300 rounded-full">
              {dealServices.length}
            </span>
          </h2>
          <div className="flex items-center gap-2 pr-4 md:pr-0">
            {deal.status === 'closed' && (
              <button
                onClick={handleManageCosts}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-[#0F7B6C] text-white rounded hover:bg-[#0a6257] transition-colors"
              >
                <PlusCircle className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Gestisci Costi Effettivi</span>
                <span className="md:hidden">Costi</span>
              </button>
            )}
          </div>
        </div>

        {/* Tabella servizi - va al bordo del contenitore */}
        <DealServiceList
          dealServices={dealServices}
          onEdit={handleEditService}
          onDelete={handleDeleteService}
          onReorder={handleReorderServices}
          dealStatus={deal.status}
          dealId={deal.id}
        />
        
        {isReordering && (
          <div className="mt-2 text-sm text-blue-600 dark:text-blue-400">
            <span className="inline-block animate-pulse">
              Aggiornamento dell'ordine in corso...
            </span>
          </div>
        )}
      </div>

    </div>
  );
});

export default DealServicesSection;